import React, { useState } from "react";
import {
  Modal,
  Switch,
  Select,
  DatePicker,
  Button,
  message,
  notification,
} from "antd";
import {
  FileText,
  ClipboardEdit,
  Shuffle,
  Calendar,
  FileTextIcon,
  X,
} from "lucide-react";
import {
  useGetFormQuestionsQuery,
  useGetFormUsersQuery,
  useSubmitFormToOrganizationMutation,
} from "../../store/api/apiSlice";
import dayjs from "dayjs";

const SubmitFormModal = ({ visible, onClose, formData }) => {
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [aggregatedSubmission, setAggregatedSubmission] = useState(false);
  const [randomizeResponses, setRandomizeResponses] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  const formId = formData?._id;
  const organizationId = formData?.organization?._id;

  // Fetch questions and users data using RTK Query
  const { data: questionsData } = useGetFormQuestionsQuery(formId, {
    skip: !formId,
  });
  const { data: usersData, isLoading: usersLoading } = useGetFormUsersQuery(
    formId,
    { skip: !formId }
  );
  const [submitFormToOrganization] = useSubmitFormToOrganizationMutation();

  const handleSubmit = async () => {
    try {
      const submitData = {
        organization: organizationId,
        completed: includeCompleted,
        aggregated: aggregatedSubmission,
        randomized: randomizeResponses,
        questions: selectedQuestions,
        users: selectedUsers,
        duration: dateRange
          ? {
              from: dayjs(dateRange[0]).unix().toString(),
              to: dayjs(dateRange[1]).unix().toString(),
            }
          : undefined,
      };

      await submitFormToOrganization({ id: formId, data: submitData }).unwrap();
      // Success notification is handled by the RTK Query mutation in apiSlice.js
      onClose();
    } catch (error) {
      // Handle unexpected errors that might not be caught by RTK Query
      console.error("Error submitting form:", error);
      message.error(error?.message || "An unexpected error occurred");
    }
  };

  const questionOptions =
    questionsData?.data?.map((question) => ({
      value: question._id,
      label: question.title || question.text || "Untitled Question",
    })) || [];

  const userOptions =
    usersData?.data?.map((user) => ({
      value: user._id,
      label: user.full_name || user.email || "Anonymous",
    })) || [];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      closeIcon={
        <div className="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center">
          <X className="text-gray-400" size={20} />
        </div>
      }
      title={
        <h3 className="text-xl font-semibold mb-6">
          Submit Form to Organisation
        </h3>
      }
    >
      <div className="space-y-6">
        {/* Switches */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center">
              <FileText className="text-gray-400" size={20} />
            </div>
            <span className="text-gray-700">Include Only Completed Forms</span>
          </div>
          <Switch
            checked={includeCompleted}
            onChange={setIncludeCompleted}
            className={includeCompleted ? "bg-[#202835]" : "bg-gray-200"}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center">
              <ClipboardEdit className="text-gray-400" size={20} />
            </div>
            <span className="text-gray-700">Aggregated Submission</span>
          </div>
          <Switch
            checked={aggregatedSubmission}
            onChange={setAggregatedSubmission}
            className={aggregatedSubmission ? "bg-[#202835]" : "bg-gray-200"}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-gray-100 rounded-full w-10 h-10 flex items-center justify-center">
              <Shuffle className="text-gray-400" size={20} />
            </div>
            <span className="text-gray-700">Randomize User Responses</span>
          </div>
          <Switch
            checked={randomizeResponses}
            onChange={setRandomizeResponses}
            className={randomizeResponses ? "bg-[#202835]" : "bg-gray-200"}
          />
        </div>

        {/* Questions Select */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Form Questions
          </label>
          <Select
            mode="multiple"
            value={selectedQuestions}
            onChange={setSelectedQuestions}
            className="w-full !rounded-full h-12"
            popupClassName="rounded-xl"
            options={questionOptions}
            allowClear
            // suffixIcon={<X size={16} className="text-gray-400" />}
            placeholder="Select questions"
            loading={!questionsData}
          />
        </div>

        {/* Users Select */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Select Users
          </label>
          <Select
            mode="multiple"
            value={selectedUsers}
            onChange={setSelectedUsers}
            className="w-full !rounded-full h-12"
            popupClassName="rounded-xl"
            options={userOptions}
            allowClear
            placeholder="Select users"
            loading={usersLoading}
            notFoundContent={usersLoading ? "Loading..." : "No users found"}
          />
        </div>

        {/* Date Range */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Duration of Responses (optional)
          </label>
          <DatePicker.RangePicker
            className="w-full !rounded-full h-12"
            value={dateRange}
            onChange={setDateRange}
            allowClear
            placeholder={["Select a date range--"]}
            popupClassName="date-range-popup-mobile"
            getPopupContainer={() => document.body} // Changed to use document.body
            dropdownAlign={{
              points: ["tl", "bl"], // Align the top-left of popup with bottom-left of input
              offset: [0, 4],
              overflow: {
                adjustX: 1,
                adjustY: 1,
              },
            }}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 items-center">
          {/* <Button
            className="flex-1 rounded-2xl h-12 bg-[#202835] hover:bg-[#202835]/90 text-white border-0 flex items-center justify-center gap-2"
            icon={<FileTextIcon size={16} />}
          >
            Export CSV
          </Button> */}
          {/* <span className="text-gray-400">or</span> */}
          <Button
            onClick={handleSubmit}
            className="flex-1 rounded-2xl h-12 bg-[#202835] hover:bg-[#202835]/90 text-white border-0"
          >
            Submit to Organisation
          </Button>
        </div>
      </div>

      <style jsx global>{`
        .ant-modal-content {
          padding: 24px !important;
          border-radius: 16px !important;
        }
        .ant-select-selector {
          border-radius: 9999px !important;
          height: 48px !important;
          padding: 8px 16px !important;
        }
        .ant-picker {
          border-radius: 9999px !important;
        }
        .ant-switch {
          background: #e5e7eb;
        }
        .ant-switch-checked {
          background: #18181b !important;
        }
      `}</style>
    </Modal>
  );
};

export default SubmitFormModal;
